import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./aIChatDetailsControl.scss";
import JSONPretty from 'react-json-pretty';
import { IconControl } from "spark-core-dx/components";

export default function AIChatDetailsControl({ aiChat: aiChatProp, height = 560, defaultTab = "flow" }) {
  const aiChat = aiChatProp;
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [tab, setTab] = useState(defaultTab);
  const [copied, setCopied] = useState(false);

  const messages = useMemo(() => {
    const arr = Array.isArray(aiChat?.ChatMessages) ? [...aiChat.ChatMessages.filter(x => x.PromptKey != 'none')] : [];
    return arr
      .map((m, i) => ({ ...m, __index: i }))
      .sort((a, b) => (a?.Sequence ?? a.__index) - (b?.Sequence ?? b.__index));
  }, [aiChat]);

  const selected = messages[selectedIdx] || null;

  useEffect(() => {
    if (aiChat !== null) {
      const msgs = aiChat?.ChatMessages;
      if (!Array.isArray(msgs) || msgs.length === 0) return;
      const first = msgs.find(m => String(m?.PromptKey || "").toLowerCase() !== "thinkingmessage");
      setSelectedIdx(Math.max(0, (first?.Sequence ?? 1) - 1));
    }
  }, [aiChat]);

  const roleMeta = {
    user: { color: "#3b82f6", border: "#60a5fa", glyph: "👤" },
    assistant: { color: "#10b981", border: "#34d399", glyph: "🤖" },
    tool: { color: "#f59e0b", border: "#fbbf24", glyph: "🔧" },
    system: { color: "#4b5563", border: "#9ca3af", glyph: "🛡️" },
    error: { color: "#ef4444", border: "#f87171", glyph: "⚠️" },
    default: { color: "#64748b", border: "#94a3b8", glyph: "💬" },
  };

  const getRole = (role) => (role ? String(role).toLowerCase() : "default");
  const getGlyph = (role, hasOperation) => {
    const r = getRole(role);
    if (r === "system" && hasOperation) return "✅";
    return (roleMeta[r] || roleMeta.default).glyph;
  };

  // ----- helpers for copy -----
  const getRawText = useCallback((x) => {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (typeof x === "object") {
      return String(x.Raw ?? x.raw ?? x.content ?? x.text ?? x.value ?? JSON.stringify(x));
    }
    return String(x);
  }, []);

  const getModelResponseText = useCallback(() => {
    if (!selected && !aiChat) return "";
    // Prefer specific message’s model output first
    const output = selected?.Output;
    const respMsg = selected?.ResponseMessage;
    const convResp = aiChat?.ModelResponse;
    return String(output ?? respMsg ?? convResp ?? "");
  }, [selected, aiChat]);

  const handleCopyAllPromptSections = useCallback(async () => {
    const list = Array.isArray(selected?.PromptSections) ? selected.PromptSections : [];
    const promptsBlock = list.map(getRawText).filter(s => (s || "").trim().length > 0).join("\n\n");
    const modelBlock = (getModelResponseText() || "").trim();

    // Build payload with headers
    const parts = [];
    if (promptsBlock) {
      parts.push("Prompts", promptsBlock);
    }
    if (modelBlock) {
      parts.push("Model Response", modelBlock);
    }
    const payload = parts.join("\n\n");

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const ta = document.createElement("textarea");
        ta.value = payload;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore
    }
  }, [selected, getRawText, getModelResponseText]);

  return (
    <div className="ai-chat-details">
      {/* Tabs */}
      <div className="ai-tabs">
        <button className={`ai-tab ${tab === "flow" ? "active" : ""}`} onClick={() => setTab("flow")}>Orchestration Flow</button>
        <button className={`ai-tab ${tab === "info" ? "active" : ""}`} onClick={() => setTab("info")}>Information</button>
      </div>

      {tab === "flow" && (
        <div className="ai-grid">
          {/* Left column */}
          <div className="ai-col ai-left">
            <div className="ai-scroll">
              <ul className="ai-list">
                {messages.map((m, idx) => {
                  const role = getRole(m.Role);
                  const meta = roleMeta[role] || roleMeta.default;
                  const active = idx === selectedIdx;
                  const name = m.OperationKey || m.PromptKey || "Chat";
                  if (String(name).toLowerCase() === 'thinkingmessage') return null;

                  return (
                    <li key={`msg-${m.__index}-${m.Sequence ?? idx}`} className="ai-li">
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <button
                          onClick={() => setSelectedIdx(idx)}
                          title={`${m.Role || "role"}: ${name}${m.Action ? ` (Action: ${m.Action})` : ""}`}
                          aria-label={`${m.Role || "role"}: ${name}`}
                          className="ai-iconbtn"
                          style={{ borderColor: meta.border, boxShadow: active ? "0 0 0 4px rgba(0,0,0,0.12)" : "none" }}
                        >
                          <div className="ai-icon" style={{ background: meta.color }}>{getGlyph(m.Role, !!m.OperationKey)}</div>
                        </button>
                        {idx < messages.length - 1 && (
                          <svg className="ai-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M19 12l-7 7-7-7" /></svg>
                        )}
                      </div>
                      <div className="ai-labels">
                        <div className="ai-name">{String(name).replace("Operation-", "")}</div>
                        {m.Action && <div className="ai-action">Action:  {m.Action}</div>}
                      </div>
                    </li>
                  );
                })}
                {messages.length === 0 && (
                  <div style={{ fontSize: 14, opacity: 0.7, padding: 16 }}>No messages.</div>
                )}
              </ul>
            </div>
          </div>

          {/* Right column */}
          <div className="ai-col">
            <div className="ai-right-scroll">
              {selected ? (
                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <div className="ai-details">
                    <div style={{ gridColumn: "span 12" }}>
                      <KV label="AI Role" value={selected.Role?.toUpperCase?.()} />
                      <KV label="Action" value={selected.Action} />
                      {selected.OperationKey && <KV label="Operation" value={selected.OperationKey.replace("Operation-", "")} />}
                      {selected.PromptKey && selected.PromptKey?.toLowerCase() !== "none" && <KV label="Prompt" value={selected.PromptKey} />}
                      <KV label="Tokens (In / Out)" value={safeNum(selected.InputTokenCount) + " / " + safeNum(selected.OutputTokenCount)} />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Section title="Input Message" body={selected.InputMessage} />
                    </div>

                    {selected.ResponseMessage && (
                      <div style={{ gridColumn: "1 / -1" }}>
                        <Section title="Response Message" body={selected.ResponseMessage} />
                      </div>
                    )}
                    {selected.Output && selected.Output !== selected.ResponseMessage && (
                      <div style={{ gridColumn: "1 / -1" }}>
                        <Section title="Model Output" body={selected.Output} />
                      </div>
                    )}
                  </div>

                  {Array.isArray(selected.PromptSections) && selected.PromptSections.length > 0 && (
                    <div style={{ padding: 16 }}>
                      {/* Header + Copy button */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, flex: "0 0 auto" }}>Prompt Sections</div>
                        <IconControl
                          onClick={handleCopyAllPromptSections}
                          iconKey="clone"
                          width="1rem"
                          height="1rem"
                          title="Copy Prompts + Model Response"
                        />
                        {copied && (
                          <span style={{ fontSize: ".75rem", color: "var(--success)" }}>Copied!</span>
                        )}
                      </div>

                      {/* Accordion */}
                      <div className="ai-accordion">
                        {selected.PromptSections.map((text, i) => {
                          const raw = getRawText(text);
                          const header = `Section ${i + 1}`;
                          return (
                            <details key={`ps-${i}`}>
                              <summary>{header}</summary>
                              <div className="content">{raw}</div>
                            </details>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: 24, fontSize: 14, opacity: 0.7 }}>Select a message from the flow to see details.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "info" && (
        <div className="ai-grid" style={{ gridTemplateColumns: "1fr" }}>
          <div className="ai-col">
            <div className="ai-details" style={{ gridTemplateColumns: "repeat(12, 1fr)" }}>
              <div style={{ gridColumn: "span 6" }}>
                <KV label="Conversation Model" value={aiChat?.ConversationModel} />
                <KV label="Total Tokens (in / out)" value={aiChat?.TotalInputTokens + " / " + aiChat?.TotalOutputTokens} />
              </div>

              <div style={{ gridColumn: "span 6" }}>
                <KV label="Status" value={aiChat?.AIOutcomeStatus} />
                <KV label="Temperature" value={typeof aiChat?.Temperature === "number" ? aiChat.Temperature.toFixed(2) : aiChat?.Temperature} />
              </div>
              {aiChat?.Input && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Section title="User Input Message" body={aiChat.Input} />
                </div>
              )}
              {aiChat?.ModelResponse && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Section className="large-section" title="Final Response Message" body={aiChat.ModelResponse} />
                </div>
              )}
              {aiChat?.DisplayErrorMessage && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Section title="Error Message" body={aiChat.DisplayErrorMessage} />
                </div>
              )}
              {aiChat?.Summary && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Section title="Summary" body={aiChat.Summary} />
                </div>
              )}
              {aiChat?.History && false && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Section title="History" body={aiChat.History} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  if (value === undefined || value === null || value === "") return (
    <div className="ai-kv"><label>{label}:</label><span>—</span></div>
  );
  return (
    <div className="ai-kv"><label>{label}:</label><span style={{ wordBreak: "break-word" }}>{String(value)}</span></div>
  );
}

const aiLightTheme = {
  main: `
    line-height:1.5;
    font-size:0.9286rem;
    color: var(--primary-text, #121212);
    background: var(--primary-background, #f5f5f5);
    border: 1px solid var(--primary-border, #C1C1C1);
    border-radius: var(--control-border-radius, 4px);
    padding: 0.8571rem;
    width:100%;
    max-height:10rem;
  `,
  error: `color: var(--error, #A41034);`,
  key: `color: var(--code-key, #0d47a1);`,
  string: `color: var(--code-string, #1f7b3e);`,
  value: `color: var(--code-value, #6a1b9a);`,
  boolean: `color: var(--code-boolean, #b26a00);`,
  number: `color: var(--code-number, #1565c0);`,
};

function SafeJSON({ data }) {
  let parsed = data;
  let isJson = true;

  if (typeof data === 'string') {
    try { parsed = JSON.parse(data); }
    catch { isJson = false; }
  }

  return isJson ? (
    <JSONPretty data={parsed} theme={aiLightTheme} />
  ) : (
    <div className="ai-pre">{data}</div>
  );
}

function Section({ title, body, className }) {
  if (!body) return null;
  return (
    <div className={`ai-section ${className || ""}`}>
      <h4>{title}</h4>
      {SafeJSON({ data: body })}
    </div>
  );
}

function safeNum(n) {
  return typeof n === "number" && !Number.isNaN(n) ? n : "—";
}
